import React, { useMemo, useState } from "react";
import { ArrowLeftRight, Download, Expand } from "lucide-react";
import { fmt } from "../../../../services/erpApi";
import { ODOO } from "./crmV2Helpers";

const monthKey = (l) => {
  const raw = l.date_open || l.create_date || l.date_deadline;
  if (!raw) return "No date";
  const s = String(raw).slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(s)) return "No date";
  return s;
};

const monthLabel = (k) => {
  if (k === "No date") return k;
  const [y, m] = k.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
};

const CrmV2PivotView = ({ leads, stages, loading, onExport }) => {
  const [measure, setMeasure] = useState("exp"); // exp | count
  const [flipped, setFlipped] = useState(false);
  const [measuresOpen, setMeasuresOpen] = useState(false);

  const stageCols = useMemo(
    () => stages.filter((s) => s.is_active !== false).sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [stages]
  );

  const pivot = useMemo(() => {
    const rowKeys = new Set();
    const colKeys = stageCols.map((s) => s.name);
    const grid = new Map(); // `${row}||${col}` -> {count, exp}

    for (const l of leads) {
      const row = monthKey(l);
      rowKeys.add(row);
      const st = stages.find((s) => String(s.id) === String(l.stage_id));
      const col = st?.name || "No stage";
      if (!colKeys.includes(col)) colKeys.push(col);
      const key = `${row}||${col}`;
      const a = grid.get(key) || { count: 0, exp: 0 };
      a.count += 1;
      a.exp += Number(l.expected_revenue) || 0;
      grid.set(key, a);
    }

    const rows = [...rowKeys].sort();
    const cols = colKeys;

    const cell = (r, c) => {
      const a = grid.get(`${r}||${c}`) || { count: 0, exp: 0 };
      return measure === "count" ? a.count : a.exp;
    };

    const rowTotals = rows.map((r) => cols.reduce((s, c) => s + cell(r, c), 0));
    const colTotals = cols.map((c) => rows.reduce((s, r) => s + cell(r, c), 0));
    const grand = colTotals.reduce((s, v) => s + v, 0);

    return { rows, cols, cell, rowTotals, colTotals, grand };
  }, [leads, stages, stageCols, measure]);

  const fmtCell = (v) => {
    if (!v) return "";
    if (measure === "count") return String(v);
    return (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // flipped: rows = stages, cols = months
  const display = useMemo(() => {
    if (!flipped) {
      return {
        rowHeaders: pivot.rows.map(monthLabel),
        rowKeys: pivot.rows,
        colHeaders: pivot.cols,
        get: (ri, ci) => pivot.cell(pivot.rows[ri], pivot.cols[ci]),
        rowTotals: pivot.rowTotals,
        colTotals: pivot.colTotals,
        grand: pivot.grand,
        corner: "Date",
      };
    }
    return {
      rowHeaders: pivot.cols,
      rowKeys: pivot.cols,
      colHeaders: pivot.rows.map(monthLabel),
      get: (ri, ci) => pivot.cell(pivot.rows[ci], pivot.cols[ri]),
      rowTotals: pivot.colTotals,
      colTotals: pivot.rowTotals,
      grand: pivot.grand,
      corner: "Stage",
    };
  }, [flipped, pivot]);

  const th = {
    padding: "8px 10px",
    fontSize: 11,
    fontWeight: 700,
    color: ODOO.muted,
    border: `1px solid ${ODOO.border}`,
    background: "#f8f9fa",
    whiteSpace: "nowrap",
    textAlign: "right",
  };

  return (
    <div style={{ background: "#fff", borderTop: `1px solid ${ODOO.border}`, minHeight: "calc(100vh - 200px)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", flexWrap: "wrap" }}>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setMeasuresOpen((o) => !o)}
            style={{
              background: ODOO.primary,
              color: "#fff",
              border: "none",
              borderRadius: 3,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.03em",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            MEASURES: {measure === "exp" ? "Expected Revenue" : "Count"} ▾
          </button>
          {measuresOpen && (
            <>
              <div onClick={() => setMeasuresOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 41, background: "#fff", border: `1px solid ${ODOO.border}`, borderRadius: 3, marginTop: 2, minWidth: 200, boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}>
                {[
                  { id: "exp", label: "Expected Revenue" },
                  { id: "count", label: "Count" },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setMeasure(m.id); setMeasuresOpen(false); }}
                    style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: measure === m.id ? ODOO.primarySoft : "transparent", padding: "8px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button type="button" title="Flip axis" onClick={() => setFlipped((f) => !f)} style={iconTool}>
          <ArrowLeftRight size={15} />
        </button>
        <button type="button" title="Expand" style={iconTool}>
          <Expand size={15} />
        </button>
        {onExport && (
          <>
            <button type="button" title="Export Excel" onClick={() => onExport("xlsx")} style={iconTool}>
              <Download size={15} />
            </button>
            <button type="button" title="Rapport PDF" onClick={() => onExport("pdf")} style={{ ...iconTool, color: ODOO.primary, borderColor: ODOO.primary }}>
              <Download size={15} />
            </button>
          </>
        )}
        {loading && <span style={{ fontSize: 12, color: ODOO.muted }}>Loading…</span>}
        <span style={{ marginLeft: "auto", fontSize: 12, color: ODOO.muted }}>
          {measure === "exp" ? fmt.money(display.grand) : `${display.grand} records`}
        </span>
      </div>

      <div style={{ overflow: "auto", padding: "0 0 16px" }}>
        <table style={{ borderCollapse: "collapse", minWidth: 640, margin: "0 16px" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>{display.corner}</th>
              {display.colHeaders.map((c) => (
                <th key={c} style={th}>
                  <div>{c}</div>
                  <div style={{ fontWeight: 500, color: "#adb5bd", fontSize: 10 }}>
                    {measure === "exp" ? "Expected Revenue" : "Count"}
                  </div>
                </th>
              ))}
              <th style={th}>
                <div>Total</div>
                <div style={{ fontWeight: 500, color: "#adb5bd", fontSize: 10 }}>
                  {measure === "exp" ? "Expected Revenue" : "Count"}
                </div>
              </th>
            </tr>
            <tr>
              <td style={{ ...th, textAlign: "left", fontWeight: 700, color: ODOO.text }}>Total</td>
              {display.colTotals.map((v, i) => (
                <td key={i} style={{ ...th, color: ODOO.text, fontWeight: 700 }}>{fmtCell(v) || (measure === "count" ? "0" : "0.00")}</td>
              ))}
              <td style={{ ...th, color: ODOO.text, fontWeight: 700 }}>{fmtCell(display.grand) || (measure === "count" ? "0" : "0.00")}</td>
            </tr>
          </thead>
          <tbody>
            {display.rowHeaders.map((rh, ri) => (
              <tr key={display.rowKeys[ri]}>
                <td style={{ padding: "7px 10px", border: `1px solid ${ODOO.border}`, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {rh}
                </td>
                {display.colHeaders.map((_, ci) => (
                  <td key={ci} style={{ padding: "7px 10px", border: `1px solid ${ODOO.border}`, fontSize: 12.5, textAlign: "right", whiteSpace: "nowrap" }}>
                    {fmtCell(display.get(ri, ci))}
                  </td>
                ))}
                <td style={{ padding: "7px 10px", border: `1px solid ${ODOO.border}`, fontSize: 12.5, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {fmtCell(display.rowTotals[ri]) || (measure === "count" ? "0" : "")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const iconTool = {
  width: 32,
  height: 32,
  border: `1px solid ${ODOO.border}`,
  background: "#fff",
  borderRadius: 3,
  cursor: "pointer",
  color: ODOO.muted,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

export default CrmV2PivotView;
